// 051 parte (d): CONTROLES PRENATALES (5 filas) y POSTPARTO (8 filas) contra el papel (form. 051 mayo/2016, pág. 178 del PDF oficial).
//
// El papel trae una tabla de 5 renglones de control prenatal y otra de 8 de postparto: aquí cada renglón es una fila con TODAS las
// columnas impresas. Las columnas con círculos "no / si" (ganancia adecuada de peso, sangrado vaginal) son tri-estado (Sí / No / sin
// responder) y el "No" llega al `Par[]` firmado. "Movim. fetales" y "Est. conc." son celdas libres en el papel: texto. Las filas
// sin ningún dato no se imprimen; las demás salen con un rótulo por columna y el número de renglón del papel (un renglón vacío en
// medio no corre la numeración).
//
// Datos guardados con el modelo viejo (UNA fila plana: `{ edadGestacional, peso, ..., movimientosFetales: boolean }`) se leen con
// `leerControlesPrenatales051` / `leerPostparto051`, que los convierten a la primera fila sin migrar nada.
import type { Par } from './index';
import { leerSiNo, textoSiNo, type SiNoValor } from './camposSimplesDatos';
import type { Traducir } from './tecnicaAnestesicaDatos';

export const NUM_CONTROLES_PRENATALES = 5;
export const NUM_CONTROLES_POSTPARTO = 8;

const PC = 'formularios.f051.controlPrenatal';
const PP = 'formularios.f051.postparto';

function siNo(t: Traducir, v: SiNoValor): string {
  return textoSiNo(v, { si: t('formulario.si'), no: t('formulario.no') });
}

// ---------- Controles prenatales ----------

/** Orden de columnas del papel (de izquierda a derecha), tal como se imprimen en el PDF. */
export const COLUMNAS_PRENATAL = [
  'fecha',
  'edadGestacional',
  'peso',
  'gananciaAdecuadaPeso',
  'ta',
  'fc',
  'fr',
  'temperatura',
  'saturacion',
  'proteinuria',
  'estConc',
  'alturaUterina',
  'presentacion',
  'frecuenciaCardiacaFetal',
  'movimientosFetales',
  'sangradoVaginal',
  'examenesTratamientosScoreMama',
  'iniciales',
  'proximaCita',
] as const;

/** Columnas con círculos "no / si" en el papel. */
export const COLUMNAS_PRENATAL_SI_NO = ['gananciaAdecuadaPeso', 'sangradoVaginal'] as const;

export interface FilaControlPrenatal051 {
  readonly fecha: string;
  readonly edadGestacional: string;
  readonly peso: string;
  readonly gananciaAdecuadaPeso: SiNoValor;
  readonly ta: string;
  readonly fc: string;
  readonly fr: string;
  readonly temperatura: string;
  readonly saturacion: string;
  readonly proteinuria: string;
  readonly estConc: string;
  readonly alturaUterina: string;
  readonly presentacion: string;
  readonly frecuenciaCardiacaFetal: string;
  readonly movimientosFetales: string;
  readonly sangradoVaginal: SiNoValor;
  readonly examenesTratamientosScoreMama: string;
  readonly iniciales: string;
  readonly proximaCita: string;
}

export interface EstadoControlesPrenatales051 {
  readonly filas: readonly FilaControlPrenatal051[];
}

export function filaControlPrenatalVacia051(): FilaControlPrenatal051 {
  return {
    fecha: '',
    edadGestacional: '',
    peso: '',
    gananciaAdecuadaPeso: '',
    ta: '',
    fc: '',
    fr: '',
    temperatura: '',
    saturacion: '',
    proteinuria: '',
    estConc: '',
    alturaUterina: '',
    presentacion: '',
    frecuenciaCardiacaFetal: '',
    movimientosFetales: '',
    sangradoVaginal: '',
    examenesTratamientosScoreMama: '',
    iniciales: '',
    proximaCita: '',
  };
}

export function estadoInicialControlPrenatal051(): EstadoControlesPrenatales051 {
  return { filas: Array.from({ length: NUM_CONTROLES_PRENATALES }, filaControlPrenatalVacia051) };
}

function texto(v: unknown): string {
  return typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
}

/** Casilla booleana del modelo viejo → Sí; lo no marcado se queda sin responder (el modelo viejo no distinguía el No). */
function siViejo(v: unknown): SiNoValor {
  return v === true ? 'si' : leerSiNo(v);
}

function esObjeto(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function filaPrenatalDesde(v: unknown): FilaControlPrenatal051 {
  if (!esObjeto(v)) return filaControlPrenatalVacia051();
  return {
    fecha: texto(v.fecha),
    edadGestacional: texto(v.edadGestacional),
    peso: texto(v.peso),
    gananciaAdecuadaPeso: siViejo(v.gananciaAdecuadaPeso ?? v.adecuadaPeso),
    ta: texto(v.ta),
    fc: texto(v.fc),
    fr: texto(v.fr),
    temperatura: texto(v.temperatura),
    saturacion: texto(v.saturacion),
    proteinuria: texto(v.proteinuria),
    // Modelo viejo: "Est. concentrado" se llamaba esteConcentrado.
    estConc: texto(v.estConc) || texto(v.esteConcentrado),
    alturaUterina: texto(v.alturaUterina),
    presentacion: texto(v.presentacion),
    frecuenciaCardiacaFetal: texto(v.frecuenciaCardiacaFetal),
    movimientosFetales: v.movimientosFetales === true ? 'Sí' : texto(v.movimientosFetales),
    sangradoVaginal: siViejo(v.sangradoVaginal),
    examenesTratamientosScoreMama: texto(v.examenesTratamientosScoreMama),
    iniciales: texto(v.iniciales),
    proximaCita: texto(v.proximaCita),
  };
}

/** Lee lo guardado: `{ filas: [...] }` (modelo nuevo) o UNA fila plana (modelo viejo, pasa a la primera). Siempre devuelve exactamente 5 filas. */
export function leerControlesPrenatales051(valor: unknown): EstadoControlesPrenatales051 {
  const crudas: unknown[] = esObjeto(valor) && Array.isArray(valor.filas) ? valor.filas : esObjeto(valor) ? [valor] : [];
  const filas = crudas.slice(0, NUM_CONTROLES_PRENATALES).map(filaPrenatalDesde);
  while (filas.length < NUM_CONTROLES_PRENATALES) filas.push(filaControlPrenatalVacia051());
  return { filas };
}

export function filaPrenatalTieneDato(f: FilaControlPrenatal051): boolean {
  return COLUMNAS_PRENATAL.some((c) => (c === 'gananciaAdecuadaPeso' || c === 'sangradoVaginal' ? f[c] !== '' : f[c].trim() !== ''));
}

export function paresDeControlesPrenatales051(t: Traducir, e: EstadoControlesPrenatales051): Par[] {
  return e.filas.slice(0, NUM_CONTROLES_PRENATALES).flatMap((f, i) => {
    if (!filaPrenatalTieneDato(f)) return [];
    const rotulo = `${t(`${PC}.filaTitulo`)} ${i + 1}`;
    return COLUMNAS_PRENATAL.map((c) => ({
      pregunta: `${rotulo} — ${t(`${PC}.${c}`)}`,
      respuesta: c === 'gananciaAdecuadaPeso' || c === 'sangradoVaginal' ? siNo(t, f[c]) : f[c].trim(),
    }));
  });
}

// ---------- Postparto ----------

/** Orden de columnas del papel (POSTPARTO: día, hora, T°C, TA, FR, pulso, Sat., est. conc., invol. uter., loquios, SCORE MAMÁ). */
export const COLUMNAS_POSTPARTO = ['dia', 'hora', 'temperatura', 'ta', 'fr', 'pulso', 'saturacion', 'estConc', 'involucionUterina', 'loquios', 'scoreMama'] as const;

export interface FilaPostparto051 {
  readonly dia: string;
  readonly hora: string;
  readonly temperatura: string;
  readonly ta: string;
  readonly fr: string;
  readonly pulso: string;
  readonly saturacion: string;
  readonly estConc: string;
  readonly involucionUterina: string;
  readonly loquios: string;
  readonly scoreMama: string;
}

export interface EstadoPostparto051 {
  readonly filas: readonly FilaPostparto051[];
}

export function filaPostpartoVacia051(): FilaPostparto051 {
  return { dia: '', hora: '', temperatura: '', ta: '', fr: '', pulso: '', saturacion: '', estConc: '', involucionUterina: '', loquios: '', scoreMama: '' };
}

export function estadoInicialPostparto051(): EstadoPostparto051 {
  return { filas: Array.from({ length: NUM_CONTROLES_POSTPARTO }, filaPostpartoVacia051) };
}

function filaPostpartoDesde(v: unknown): FilaPostparto051 {
  if (!esObjeto(v)) return filaPostpartoVacia051();
  return {
    dia: texto(v.dia),
    hora: texto(v.hora),
    temperatura: texto(v.temperatura),
    ta: texto(v.ta),
    fr: texto(v.fr),
    pulso: texto(v.pulso),
    saturacion: texto(v.saturacion),
    // Modelo viejo: la columna "est. conc." se llamaba estadoUterino.
    estConc: texto(v.estConc) || texto(v.estadoUterino),
    involucionUterina: texto(v.involucionUterina),
    loquios: texto(v.loquios),
    scoreMama: texto(v.scoreMama),
  };
}

/** Lee lo guardado: `{ filas: [...] }` o UNA fila plana del modelo viejo (pasa a la primera). Siempre devuelve exactamente 8 filas. */
export function leerPostparto051(valor: unknown): EstadoPostparto051 {
  const crudas: unknown[] = esObjeto(valor) && Array.isArray(valor.filas) ? valor.filas : esObjeto(valor) ? [valor] : [];
  const filas = crudas.slice(0, NUM_CONTROLES_POSTPARTO).map(filaPostpartoDesde);
  while (filas.length < NUM_CONTROLES_POSTPARTO) filas.push(filaPostpartoVacia051());
  return { filas };
}

export function filaPostpartoTieneDato(f: FilaPostparto051): boolean {
  return COLUMNAS_POSTPARTO.some((c) => f[c].trim() !== '');
}

export function paresDePostparto051(t: Traducir, e: EstadoPostparto051): Par[] {
  return e.filas.slice(0, NUM_CONTROLES_POSTPARTO).flatMap((f, i) => {
    if (!filaPostpartoTieneDato(f)) return [];
    const rotulo = `${t(`${PP}.filaTitulo`)} ${i + 1}`;
    return COLUMNAS_POSTPARTO.map((c) => ({ pregunta: `${rotulo} — ${t(`${PP}.${c}`)}`, respuesta: f[c].trim() }));
  });
}
